import * as t from 'io-ts';
import {identity, isUndefined, omit, pick} from 'lodash';
import memoize from 'memoize-one';

import {RISolemnlySwearThisIsCleanHtml} from '../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {BumpStrategiesEnum} from '../../libs/shared/core/src/models/api/companyPreferencesModel';
import {
  serializeConversationOfflineMetadata,
  serializeConversationTracker,
} from '../../libs/shared/core/src/models/api/conversationPatchModel';
import {ConversationTrackerStatusesEnum} from '../../libs/shared/core/src/models/api/conversationTrackerModel';
import {
  MessageModel,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
} from '../../libs/shared/core/src/models/api/messageModel';
import {makeEnumRuntimeType} from '../../libs/shared/core/src/models/api/types/enumTypes';
import {makeOptional} from '../../libs/shared/core/src/models/api/types/optionalType';
import {HttpNetworkError, HttpTimeoutError} from '../../libs/shared/core/src/services/http/httpError';
import {
  composeUpdaters,
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createResourceTableValueUpdater,
  createUpdater,
} from '../../libs/shared/core/src/store/helpers/updateHelpers';
import {OptimisticUpdater} from '../../libs/shared/core/src/store/optimisticUpdates/optimisticUpdatesState';
import {createCountersUpdaterForConversations} from '../../src/helpers/counterHelpers';
import {
  applyTrackerBumpedOfflineId,
  autoAssignConversation,
  autoSubscribeToConversation,
  bumpConversationOnSend,
  shouldAutoAssignChannel,
  updateConversationTrackerOffline,
} from '../../src/helpers/models/conversationHelpers';
import {findDraftMessageData, isDirtyDraftMessage} from '../../src/helpers/models/messageHelpers';
import {ConversationModel} from '../../src/models/api/conversationModel';
import {findMessageQueue} from '../../src/services/tasks/specializedTaskQueues';
import {updateClientCountersForConversationsAction} from '../../src/store/data/counters/countersActions';
import {dataUpdatedAction} from '../../src/store/data/dataGroupsActions';
import {DataState} from '../../src/store/data/dataState';
import {sendMessageApiAsync} from '../../src/store/data/messages/messagesHelpers';
import {flushMessageAsyncAction} from '../../src/store/data/messages/saveMessageActions';
import {OfflineError} from '../../srcCommon/services/offline/offlineError';
import {createTaskType} from './task';

/*
 * Task.
 */

export const RSendMessageTask = createTaskType({
  name: 'sendMessage',
  payloadType: t.type({
    // When the action was taken.
    initiatedAt: t.number,

    // The message and when to send it.
    messageUid: t.string,
    sendAt: t.number,

    // The optimistic message content.
    // TODO: Calculate this dynamically from the data state to pick up changes to contacts, signatures, etc.
    optimisticContent: t.type({
      lightBody: RISolemnlySwearThisIsCleanHtml,
      body: RISolemnlySwearThisIsCleanHtml,
      blurb: t.string,
    }),

    // The current (delegated) tracker state for conflict resolution.
    teammateId: t.number,
    lastBumpedAt: makeOptional(t.number),
    offlineBumpedId: t.string,

    // The new (delegated) tracker state.
    status: makeOptional(makeEnumRuntimeType(ConversationTrackerStatusesEnum)),
    remindAt: makeOptional(t.number),

    // The new tags to add to the conversation.
    tagIds: t.readonlyArray(t.number),
  }),
  // TODO: Show optimistic updates for tags. At the time of writing, we don't do this online either.
  createOptimisticUpdater: (
    {messageUid, sendAt, optimisticContent, teammateId, lastBumpedAt, offlineBumpedId, status, remindAt},
    taskId,
  ) => {
    // Create an optimistic updater for the message.
    const sendIn = sendAt - Date.now();
    const sendStatus =
      sendIn > 60 * 1000 ? MessageStatusesEnum.DRAFT_LATER : MessageStatusesEnum.DRAFT_PENDING;
    const optimisticUpdaterForMessage = createObjectPropertyUpdater(
      'messages',
      createResourceMapValueUpdater(
        messageUid,
        createUpdater<MessageModel>((message) => {
          if (message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
            return message;
          }

          return {
            ...message,
            ...optimisticContent,
            status: sendStatus,
            sentAt: sendAt,
            // This allows the undo send action to remove the offline task.
            sendOfflineTaskId: taskId,
          };
        }),
      ),
    );

    const createOptimisticUpdaterWithData = memoize(
      (
        bumpStrategy: BumpStrategiesEnum,
        shouldAutoAssign: boolean,
        userId: number,
        shouldAutoSubscribe: boolean,
        conversationId: number,
      ): OptimisticUpdater<DataState> => {
        // If the message does not yet have a conversation, there's nothing else we need to do.
        if (!conversationId) {
          return optimisticUpdaterForMessage;
        }

        // Create an optimistic updater for the conversation.
        const optimisticUpdaterForConversation = createCountersUpdaterForConversations(
          [conversationId],
          createObjectPropertyUpdater(
            'conversations',
            createResourceTableValueUpdater(
              conversationId,
              createUpdater<ConversationModel>(
                composeUpdaters(
                  shouldAutoAssign ? autoAssignConversation(teammateId) : identity,
                  shouldAutoSubscribe ? autoSubscribeToConversation(userId) : identity,
                  updateConversationTrackerOffline({
                    teammateId,
                    lastBumpedAt,
                    offlineBumpedId,
                    status,
                    remindAt,
                  }),
                  bumpConversationOnSend(bumpStrategy, BumpStrategiesEnum.MESSAGE, teammateId),
                ),
              ),
            ),
          ),
        );

        // Compose the optimistic updaters.
        return composeUpdaters(optimisticUpdaterForMessage, optimisticUpdaterForConversation);
      },
    );

    // This is a higher order optimistic updater because we depend on company, channel, and user preferences and we don't
    // want to break memoization on the entire data state.
    return (data) => {
      const {message, channel} = findDraftMessageData(data, messageUid);
      if (!message || !channel) {
        return data;
      }

      return createOptimisticUpdaterWithData(
        data.company.preferences.sendBumpStrategy,
        shouldAutoAssignChannel(channel),
        data.userId,
        data.preferences.shouldSubscribeOnReply,
        message.conversationId,
      )(data);
    };
  },
  createPerformer:
    ({
      initiatedAt,
      messageUid,
      sendAt,
      teammateId,
      lastBumpedAt,
      offlineBumpedId,
      status,
      remindAt,
      tagIds,
    }) =>
    async (dispatch, getState, finishOptimisticUpdate) => {
      try {
        // We have to use the legacy task queue, because it is used for saving the message and so it may be holding on to
        // results from API calls.
        const messageQueue = findMessageQueue(dispatch, getState, messageUid);

        // Schedule saving the message, in case it has unsaved changes.
        const savePromise = dispatch(flushMessageAsyncAction(messageUid));

        // Schedule our turn to send the message immediately following the save (if any).
        const messageQueueTurnPromise = messageQueue.waitForTurnAsync();
        // Try/catch to ensure we balance our waitForTurnAsync() call.
        try {
          // Wait for the save (if any) and propagate any (offline) errors thrown during the save.
          await savePromise;
          // Wait for our turn to send the message.
          await messageQueueTurnPromise;

          const message = messageQueue.getResult();
          if (!message || message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
            throw new Error('The specified message could not be found.');
          }

          if (isDirtyDraftMessage(message)) {
            throw new Error('Failed to save message before performing offline send');
          }

          const sendIn = sendAt - Date.now();

          // Opt out of conflict resolution if the conversation did not yet exist at the time of sending offline.
          const trackerOfflineMetadata = isUndefined(lastBumpedAt)
            ? undefined
            : {
                teammateId,
                lastBumpedAt,
                offlineBumpedId,
              };

          // Perform the API request.
          const body = {
            type: 'outbound',
            message: {
              uid: messageUid,
              version: message.version,
            },
            meta: {
              delay: sendIn > 0 ? Math.round(sendIn / 1000) : undefined,
              tag_ids: tagIds,
              trackers: [serializeConversationTracker({teammateId, status, remindAt})],
            },
            offline: serializeConversationOfflineMetadata({
              trackers: trackerOfflineMetadata ? [trackerOfflineMetadata] : undefined,
              initiatedAt,
            }),
          };
          const serverDataGroup = await sendMessageApiAsync(message, body);

          // Post-process the API response.
          const serverDataGroupWithOfflineId = applyTrackerBumpedOfflineId(
            teammateId,
            offlineBumpedId,
            serverDataGroup,
          );
          const resultDataGroup = serverDataGroupWithOfflineId;

          // Update our state + finish the optimistic update + update client-side counters.
          // TODO: Make this prettier and reduce duplication.
          dispatch(
            updateClientCountersForConversationsAction(
              (resultDataGroup.conversations || []).map((c) => c.id),
              () => {
                finishOptimisticUpdate(dataUpdatedAction(omit(resultDataGroup, 'messages')));
              },
            ),
          );

          messageQueue.provideResultAsync(pick(resultDataGroup, 'messages'));
        } catch (error) {
          messageQueueTurnPromise.then(() => {
            messageQueue.reportError();
          });
          throw error;
        }
      } catch (error) {
        if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
          throw new OfflineError(error);
        }

        throw error;
      }
    },
});
